import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatPoseAngles, overlayJointsForSample } from "../../utils/poseTimeline";
import { resolveFhPhaseAtMs } from "../../utils/poseHeuristics";
import {
  estimateTrunkRotation,
  trunkRotationDegForOverlay,
} from "../../utils/trunkRotation";
import "./poseOverlays.css";

const PHASE_LABEL_KEYS = {
  preparation: ["posePhasePreparation", "Preparation"],
  forward_swing: ["posePhaseForwardSwing", "Forward swing"],
  contact: ["posePhaseContact", "Contact"],
  follow_through: ["posePhaseFollowThrough", "Follow-through"],
  recovery: ["posePhaseRecovery", "Recovery"],
};

function trunkLine(sideSample, tableCorners, t) {
  const joints = overlayJointsForSample(sideSample).joints;
  const est = estimateTrunkRotation(joints, { tableCorners });
  const deg = trunkRotationDegForOverlay(est);
  if (deg == null) return null;
  const label = t("poseTrunkRotation", "Trunk");
  return `${label}: ${Math.round(deg)}°`;
}

/**
 * Live elbow/knee angle cards: Player A always bottom-left, Player B bottom-right.
 * Player A also shows the current FH / FH topspin / BH topspin stroke phase when available.
 */
export function PoseAnglesPanel({
  sample,
  tableCorners = null,
  fhTopspinDetection = null,
  fhDetection = null,
  bhTopspinDetection = null,
  bhLeftDetection = null,
  currentTimeSec = null,
}) {
  const { t } = useTranslation();

  const activePhase = useMemo(() => {
    const tMs =
      currentTimeSec != null && Number.isFinite(Number(currentTimeSec))
        ? Math.round(Number(currentTimeSec) * 1000)
        : null;
    if (tMs == null) return null;
    return resolveFhPhaseAtMs(
      {
        fh_topspin_left: fhTopspinDetection,
        fh_left: fhDetection,
        bh_topspin_left: bhTopspinDetection,
        bh_left: bhLeftDetection,
      },
      tMs
    );
  }, [fhTopspinDetection, fhDetection, bhTopspinDetection, bhLeftDetection, currentTimeSec]);

  if (!sample) return null;

  const phaseKey = activePhase?.phaseKey || null;
  const phaseLabel = phaseKey
    ? t(PHASE_LABEL_KEYS[phaseKey]?.[0] || phaseKey, PHASE_LABEL_KEYS[phaseKey]?.[1] || phaseKey)
    : null;
  const id = activePhase?.detectionId;
  let shortLabel = t("poseFhShort", "FH");
  let ariaLabel = t("poseFhPhase", "Forehand phase");
  if (id === "fh_topspin_left") {
    shortLabel = t("poseFhTopspinShort", "TS");
    ariaLabel = t("poseFhTopspinPhase", "Forehand topspin phase");
  } else if (id === "bh_topspin_left") {
    shortLabel = t("poseBhTopspinShort", "BH");
    ariaLabel = t("poseBhTopspinPhase", "Backhand topspin phase");
  } else if (id === "bh_left") {
    shortLabel = t("poseBhShort", "BH");
    ariaLabel = t("poseBhPhase", "Backhand phase");
  }

  const trunkLeft = trunkLine(sample.left, tableCorners, t);
  const trunkRight = trunkLine(sample.right, tableCorners, t);
  const anglesLeftBody = formatPoseAngles(sample.left?.angles, null);
  const anglesRightBody = formatPoseAngles(sample.right?.angles, null);
  const playerBTitle = t("posePlayerB", "Player B");

  return (
    <div className="pose-angles-panel" aria-live="polite">
      <div className="pose-angles-panel__block pose-angles-panel__block--left">
        <div className="pose-angles-panel__title">
          {t("posePlayerA", "Player A")}
        </div>
        {phaseLabel ? (
          <div
            className={`pose-angles-panel__phase pose-angles-panel__phase--${phaseKey}`}
            aria-label={ariaLabel}
          >
            <span className="pose-angles-panel__phase-label">{shortLabel}</span>
            <span className="pose-angles-panel__phase-value">{phaseLabel}</span>
          </div>
        ) : null}
        <pre className="pose-angles-panel__angles">
          {[trunkLeft, anglesLeftBody].filter(Boolean).join("\n")}
        </pre>
      </div>
      <div className="pose-angles-panel__block pose-angles-panel__block--right">
        <pre className="pose-angles-panel__angles pose-angles-panel__angles--solo">
          {[playerBTitle, trunkRight, anglesRightBody].filter(Boolean).join("\n")}
        </pre>
      </div>
    </div>
  );
}

export default PoseAnglesPanel;
